import type { ChangeLogEntry, ChangeLogLineItem, OrderChangeLog } from '../../api/orders/types';
import { formatShortDate, formatTime } from '../../utils/formatters';
import { useTranslation } from '../../hooks/useTranslation';

// What the last rebuild did to the draft: one row per event the reconciler
// created, updated (field-level old → new), or cancelled. Sourced from the
// reconciler's write-log, so it is exhaustive by construction — anything not
// listed here was left untouched. Absent until the order has been rebuilt;
// replaced by each subsequent successful rebuild.
export function WhatChangedCard({ changeLog }: { changeLog: OrderChangeLog }) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="what-changed"
      role="note"
      className="rounded-input border border-dinnerplate-white bg-dinnerplate-white-lighter px-4 py-3"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-inter text-xs font-semibold uppercase tracking-wide text-grey">
          {t('order.change_log.title', 'What changed in the last rebuild')}
        </p>
        <p className="shrink-0 font-inter text-xs leading-normal text-grey">
          {formatShortDate(changeLog.created_at)}, {formatTime(changeLog.created_at)}
        </p>
      </div>

      <ul className="mt-2 space-y-2">
        {changeLog.changes.map((entry) => (
          <ChangeRow key={`${entry.event_id}-${entry.action}`} entry={entry} />
        ))}
      </ul>
    </div>
  );
}

function ChangeRow({ entry }: { entry: ChangeLogEntry }) {
  const { t } = useTranslation();

  const badge = {
    created: {
      text: t('order.change_log.created', 'New'),
      className: 'text-confirmed-green',
    },
    updated: {
      text: t('order.change_log.updated', 'Updated'),
      className: 'text-off-black',
    },
    cancelled: {
      text: t('order.change_log.cancelled', 'Removed'),
      className: 'text-chilli-red',
    },
  }[entry.action];

  return (
    <li className="font-inter text-sm leading-normal text-off-black">
      <span className={`font-semibold ${badge.className}`}>{badge.text}</span>{' '}
      <span className={entry.action === 'cancelled' ? 'line-through opacity-70' : undefined}>
        {entry.label}
      </span>
      {entry.action === 'updated' && (
        <ul className="mt-0.5 space-y-0.5 pl-4 text-xs text-grey">
          {Object.entries(entry.fields ?? {}).map(([field, [oldValue, newValue]]) => (
            <li key={field}>
              {fieldLabel(field)}: {formatValue(field, oldValue) ?? '—'} →{' '}
              <span className="font-semibold text-off-black">
                {formatValue(field, newValue) ?? '—'}
              </span>
            </li>
          ))}
          {visibleLineItems(entry).map((item, index) => (
            <LineItemRow key={index} item={item} />
          ))}
        </ul>
      )}
    </li>
  );
}

// Line-item quantities follow pax, so on a pax change every line reports the
// new count — noise the "Pax: 60 → 70" row already conveys. Hide those
// pax-following rows; keep genuine line changes (added / removed / swapped, or
// a quantity that diverges from the new pax).
function visibleLineItems(entry: ChangeLogEntry): ChangeLogLineItem[] {
  const items = entry.line_items ?? [];
  const newPax = entry.fields?.pax_count?.[1];
  if (newPax == null) return items;

  return items.filter((item) => {
    const swapped = Boolean(item.was && item.was !== item.name);
    const quantityOnly =
      item.action === 'updated' && !swapped && item.fields?.quantity != null;
    const followsPax = quantityOnly && String(item.fields?.quantity) === String(newPax);
    return !followsPax;
  });
}

function LineItemRow({ item }: { item: ChangeLogLineItem }) {
  const { t } = useTranslation();

  if (item.action === 'added') {
    return (
      <li className="text-confirmed-green">
        + {item.name} {item.quantity != null && `×${item.quantity}`}
      </li>
    );
  }
  if (item.action === 'removed') {
    return (
      <li className="text-chilli-red">
        − {item.name} {item.quantity != null && `×${item.quantity}`}
      </li>
    );
  }
  // A dish swap keeps the line: say what it was; a quantity change says the new count.
  const swapped = item.was && item.was !== item.name;
  return (
    <li>
      {swapped
        ? `${item.was} → ${item.name}`
        : `${item.name}${item.fields?.quantity != null ? ` ${t('order.change_log.quantity', '×')}${item.fields.quantity}` : ''}`}
    </li>
  );
}

// Field names come from the extraction schema / event columns; label the known
// ones for reps, humanize the rest so new fields degrade gracefully.
const FIELD_LABELS: Record<string, string> = {
  pax_count: 'Pax',
  price_per_pax: 'Price/pax',
  scheduled_at: 'Date & time',
  pre_setup_date_time: 'Setup time',
  serving_time: 'Serving time',
  form: 'Format',
  product_line: 'Service',
  venue_name: 'Venue',
  venue_address: 'Address',
  venue_unit: 'Unit',
  postal_code: 'Postal code',
  primary_host_name: 'Host',
  primary_host_number: 'Host number',
  secondary_host_name: 'Secondary host',
  secondary_host_number: 'Secondary host number',
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replaceAll('_', ' ');
}

function formatValue(field: string, value: string | null): string | null {
  if (value == null || value === '') return null;
  if (field === 'scheduled_at' || field === 'pre_setup_date_time')
    return `${formatShortDate(value)}, ${formatTime(value)}`;

  return value;
}
